import { useMemo, useState, type KeyboardEvent } from 'react'
import { Plus, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { DEFAULT_SKILL_SUGGESTIONS } from '@/lib/skillSuggestions'
import { useGetSkillsQuery } from '@/redux/service/skills'

interface SkillsInputProps {
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  suggestions?: string[]
}

const SkillsInput = ({
  value,
  onChange,
  placeholder,
  suggestions,
}: SkillsInputProps) => {
  const [draft, setDraft] = useState('')
  // Fall back to the bundled list if the request hasn't resolved yet so the
  // input is never empty on first render.
  const { data } = useGetSkillsQuery(undefined, { skip: !!suggestions })
  const liveSuggestions = useMemo(() => {
    if (suggestions) return suggestions
    if (data?.data) return data.data.map(s => s.name)
    return DEFAULT_SKILL_SUGGESTIONS
  }, [suggestions, data])

  const valueLower = useMemo(
    () => new Set(value.map(s => s.toLowerCase())),
    [value]
  )

  const remainingSuggestions = useMemo(() => {
    const q = draft.trim().toLowerCase()
    return liveSuggestions
      .filter(s => !valueLower.has(s.toLowerCase()))
      .filter(s => (q ? s.toLowerCase().includes(q) : true))
      .slice(0, 12)
  }, [liveSuggestions, valueLower, draft])

  const commit = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed) return
    if (valueLower.has(trimmed.toLowerCase())) {
      setDraft('')
      return
    }
    onChange([...value, trimmed])
    setDraft('')
  }

  const remove = (skill: string) => {
    onChange(value.filter(s => s !== skill))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit(draft)
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  return (
    <div className='space-y-2'>
      {value.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {value.map(skill => (
            <span
              key={skill}
              className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20 shadow-sm'
            >
              {skill}
              <button
                type='button'
                onClick={() => remove(skill)}
                className='text-primary/60 hover:text-primary transition-colors'
              >
                <X className='w-3.5 h-3.5' />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(draft)}
        placeholder={placeholder ?? 'Type a skill, then press Enter'}
        className='h-10 border-border rounded-lg focus:ring-1 focus:ring-primary transition-all'
      />
      {remainingSuggestions.length > 0 && (
        <div className='pt-1'>
          <p className='text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2 px-1'>
            Suggestions
          </p>
          <div className='flex flex-wrap gap-1.5'>
            {remainingSuggestions.map(s => (
              <button
                type='button'
                key={s}
                onClick={() => commit(s)}
                className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border border-border bg-background hover:bg-muted hover:border-primary/30 text-foreground transition-all duration-200'
              >
                <Plus className='w-3 h-3 text-muted-foreground' />
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SkillsInput
